<template>
  <div class="ai-dos-admin">
    <aside class="ai-dos-sidebar">
      <div class="sidebar-header">
        <p class="eyebrow">Admin</p>
        <h1>AI DOS</h1>
        <p>
          Monitor deterministic sizing verification and future AI DOS tools.
        </p>
      </div>

      <nav class="sidebar-nav" aria-label="AI DOS admin navigation">
        <RouterLink
          v-for="item in navItems"
          :key="item.to.name"
          :to="item.to"
          class="nav-link"
          exact-active-class="active"
        >
          <span class="nav-title">{{ item.title }}</span>
          <span class="nav-description">{{ item.description }}</span>
        </RouterLink>
      </nav>
    </aside>

    <main class="ai-dos-content">
      <RouterView />
    </main>
  </div>
</template>

<script setup lang="ts">
import { RouterLink, RouterView } from "vue-router";

const navItems = [
  {
    title: "Overview",
    description: "Feature map and operations backlog",
    to: { name: "AiDosAdminOverview" }
  },
  {
    title: "Calculation Parity",
    description: "Browser/server consistency — not AI quality",
    to: { name: "AiDosSizingParity" }
  },
  {
    title: "Material Search",
    description: "Inspect catalog filters and availability",
    to: { name: "AiDosEndpointTester" }
  },
  {
    title: "ERP Mappings",
    description: "Review catalog-to-SAP identities",
    to: { name: "AiDosErpMappings" }
  },
  {
    title: "Verified Candidates",
    description: "Generate trace-backed ESP packages",
    to: { name: "AiDosCandidates" }
  },
  {
    title: "Design Evaluation",
    description: "Won quotes, human vs AI, reviewer labels",
    to: { name: "AiDosRankingValidation" }
  },
  {
    title: "Calculation Tester",
    description: "Run ad-hoc calculations and inspect traces",
    to: { name: "AiDosCalculationTester" }
  },
  {
    title: "AgentCore",
    description: "Test runtime, gateway, and MCP tools",
    to: { name: "AiDosAgentCore" }
  }
];
</script>

<style scoped lang="scss">
.ai-dos-admin {
  min-height: calc(100vh - 4rem);
  display: grid;
  grid-template-columns: 20rem minmax(0, 1fr);
  background: linear-gradient(135deg, var(--gray-100), var(--surface-0));
}

.ai-dos-sidebar {
  border-right: 1px solid var(--surface-300);
  background: var(--surface-0);
  padding: 2rem 1.25rem;
}

.sidebar-header {
  padding: 0 0.75rem 1.5rem;

  h1 {
    margin: 0;
    font-size: 2rem;
  }

  p:last-child {
    color: var(--text-color-secondary);
    line-height: 1.4;
  }
}

.eyebrow {
  margin: 0 0 0.25rem;
  color: var(--red-500);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.sidebar-nav {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.nav-link {
  border: 1px solid transparent;
  border-radius: 0.75rem;
  color: var(--text-color-secondary);
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.875rem 0.75rem;
  text-decoration: none;

  &:hover,
  &.active {
    background: var(--surface-100);
    border-color: var(--surface-300);
    color: var(--text-color);
  }

  &.active {
    box-shadow: inset 3px 0 0 var(--red-500);
  }
}

.nav-title {
  font-weight: 700;
}

.nav-description {
  color: inherit;
  font-size: 0.85rem;
  opacity: 0.82;
}

.ai-dos-content {
  min-width: 0;
  padding: 2rem;
}

@media (max-width: 900px) {
  .ai-dos-admin {
    grid-template-columns: 1fr;
  }

  .ai-dos-sidebar {
    border-right: none;
    border-bottom: 1px solid var(--surface-300);
  }
}
</style>
